import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";

type ResizableProps = {
    initialWidth?: number;
    minWidth?: number;
    maxWidth?: number;
    autoSaveId?: string;
    fullWidthThreshold?: number;
} & PropsWithChildren;

const ResizableContext = createContext<{
    onResizeStart: (e: React.MouseEvent) => void;
} | null>(null);

export const ResizableHandle: FC<PropsWithClassName & { onClick?: () => void }> = ({ className, onClick }) => {
    const context = useContext(ResizableContext);

    if (!context) {
        throw new Error("ResizableHandle must be used within a Resizable component");
    }

    return (
        <button
            className={cn(
                "absolute right-0 top-0 h-full w-1 cursor-ew-resize rounded-full transition-colors duration-150 hover:bg-surface-03 active:bg-surface-04",
                className,
            )}
            onMouseDown={context.onResizeStart}
            onDragStart={(e) => e.preventDefault()}
            onClick={onClick}
            data-tracking-id-none
        />
    );
};

/**
 * This is a pixel based resizable element.
 * Use {@link ResizablePanel} if you need a %-based resizable element or need more advanced panel management.
 */
export const Resizable: FC<ResizableProps & PropsWithClassName> = ({
    children,
    initialWidth = 200,
    minWidth = 100,
    maxWidth = 800,
    fullWidthThreshold,
    autoSaveId,
    className,
}) => {
    const [width, setWidth] = useState(() => {
        if (autoSaveId) {
            const savedWidth = localStorage.getItem(autoSaveId);
            if (savedWidth) {
                const parsedWidth = parseInt(savedWidth, 10);
                return Math.max(minWidth, Math.min(maxWidth, parsedWidth));
            }
        }
        return initialWidth;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault(); // Prevent text selection
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !containerRef.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX - containerRect.left));

            setWidth(newWidth);
        },
        [isDragging, maxWidth, minWidth],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        if (autoSaveId) {
            localStorage.setItem(autoSaveId, width.toString());
        }
    }, [width, autoSaveId]);

    const [currentScreenWidth, setCurrentScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        if (fullWidthThreshold) {
            const handleResize = () => {
                setCurrentScreenWidth(window.innerWidth);
            };

            window.addEventListener("resize", handleResize);
            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [fullWidthThreshold]);

    const adjustedWidth = useMemo(() => {
        if (fullWidthThreshold && currentScreenWidth < fullWidthThreshold) {
            if (maxWidth === 0 && minWidth === 0) {
                return 0; // hidden
            }
            return currentScreenWidth;
        }

        return Math.max(minWidth, Math.min(maxWidth, width));
    }, [fullWidthThreshold, width, currentScreenWidth, minWidth, maxWidth]);

    return (
        <ResizableContext.Provider value={{ onResizeStart: handleMouseDown }}>
            <div ref={containerRef} className={cn("relative", className)} style={{ width: `${adjustedWidth}px` }}>
                {children}
            </div>
        </ResizableContext.Provider>
    );
};
